/**
 * Client-side UTM parameter utilities.
 * Captures the 5 standard UTM params (source, medium, campaign, content, term)
 * from URL params and persists each as an independent 90-day cookie so
 * campaign context survives navigation across pages and sessions.
 */

const UTM_NAMES = ['source', 'medium', 'campaign', 'content', 'term'] as const;
type UtmName = (typeof UTM_NAMES)[number];
export type Utms = Partial<Record<UtmName, string>>;

const COOKIE_PREFIX = '_utm_';
const NINETY_DAYS_MS = 90 * 24 * 60 * 60 * 1000;

function cookieName(name: UtmName): string {
  return `${COOKIE_PREFIX}${name}`;
}

/**
 * Capture any present utm_* params (source, medium, campaign, content, term)
 * from the current URL and store each in a 90-day cookie.
 * Missing params do not clear existing cookies — each UTM has an independent
 * lifetime driven by the last URL that set it.
 */
export function captureUtms(): void {
  if (typeof window === 'undefined') return;

  const params = new URLSearchParams(window.location.search);
  const expires = new Date(Date.now() + NINETY_DAYS_MS).toUTCString();

  for (const name of UTM_NAMES) {
    const value = params.get(`utm_${name}`);
    if (!value) continue;
    document.cookie = `${cookieName(name)}=${encodeURIComponent(
      value
    )};expires=${expires};path=/;`;
  }
}

/**
 * Read a single persisted UTM cookie value.
 */
export function getUtm(name: UtmName): string | undefined {
  if (typeof document === 'undefined') return undefined;

  const target = `${cookieName(name)}=`;
  const match = document.cookie
    .split(';')
    .map((c) => c.trim())
    .find((c) => c.startsWith(target));

  return match ? decodeURIComponent(match.slice(target.length)) : undefined;
}

/**
 * Read all persisted UTM cookies as a partial object.
 * Keys are present only when the corresponding cookie exists.
 */
export function getUtms(): Utms {
  const result: Utms = {};
  for (const name of UTM_NAMES) {
    const value = getUtm(name);
    if (value !== undefined) result[name] = value;
  }
  return result;
}

/**
 * Back-compat: capture UTM params from URL. Delegates to captureUtms().
 */
export function captureUtmSource(): void {
  captureUtms();
}

/**
 * Back-compat: read the persisted _utm_source cookie value.
 */
export function getUtmSource(): string | undefined {
  return getUtm('source');
}
